import React, { useEffect, useMemo, useRef, useState } from 'react';
import './incident-picker.css';

const MAX_RESULTS = 10;
const OTHER_LABEL = 'Other / Not Listed';

function searchableText(option) {
  return [option?.display, option?.name, option?.incidentOrderNumber]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export default function IncidentPicker({ value, onChange, options, feedStatus }) {
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [searchValue, setSearchValue] = useState(String(value || ''));
  const justEnteredManual = useRef(false);

  useEffect(() => {
    if (manualMode) {
      if (!value) {
        if (justEnteredManual.current) {
          justEnteredManual.current = false;
          return;
        }
        setManualMode(false);
        setSearchValue('');
      }
      return;
    }
    setSearchValue(String(value || ''));
  }, [value, manualMode]);

  const filtered = useMemo(() => {
    const query = String(searchValue || '').trim().toLowerCase();
    const matches = query
      ? options.filter(option => searchableText(option).includes(query))
      : options;
    return matches.slice(0, MAX_RESULTS);
  }, [options, searchValue]);

  const selectIncident = option => {
    setManualMode(false);
    setSearchValue(option.display);
    onChange(option.display);
    setOpen(false);
  };

  const selectOther = () => {
    justEnteredManual.current = true;
    setManualMode(true);
    setSearchValue(OTHER_LABEL);
    onChange('');
    setOpen(false);
  };

  const chooseListedIncident = () => {
    setManualMode(false);
    setSearchValue('');
    onChange('');
    setOpen(true);
  };

  return (
    <div className="tracking-incident-picker">
      <input
        id="tracking-incident-input"
        value={manualMode ? OTHER_LABEL : searchValue}
        readOnly={manualMode}
        onChange={event => {
          if (manualMode) return;
          setSearchValue(event.target.value);
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => { if (!manualMode) setOpen(true); }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        placeholder="Start typing incident name or order number"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="tracking-incident-options"
        required={!manualMode}
      />

      {open && !manualMode && (
        <div id="tracking-incident-options" className="tracking-incident-options" role="listbox">
          {feedStatus === 'loading' && !options.length && (
            <div className="tracking-incident-empty">Loading active incidents…</div>
          )}

          {feedStatus !== 'loading' && filtered.length === 0 && (
            <div className="tracking-incident-empty">
              No matching listed incident.
            </div>
          )}

          {filtered.map(option => (
            <button
              type="button"
              role="option"
              key={option.display}
              className="tracking-incident-option"
              onPointerDown={event => event.preventDefault()}
              onClick={() => selectIncident(option)}
            >
              <strong>{option.name}</strong>
              <small>
                {option.incidentOrderNumber || 'Recent incident'}
                {option.incidentSource ? ` · ${option.incidentSource}` : ''}
              </small>
            </button>
          ))}

          <button
            type="button"
            role="option"
            className="tracking-incident-option tracking-incident-other"
            onPointerDown={event => event.preventDefault()}
            onClick={selectOther}
          >
            <strong>{OTHER_LABEL}</strong>
            <small>Manually enter the incident name</small>
          </button>
        </div>
      )}

      {manualMode && (
        <div className="tracking-manual-incident">
          <span>Incident name</span>
          <input
            value={value}
            onChange={event => onChange(event.target.value)}
            placeholder="Enter incident name"
            autoComplete="off"
            aria-label="Manual incident name"
            autoFocus
            required
          />
          <button type="button" className="tracking-incident-switch" onClick={chooseListedIncident}>
            Choose a listed incident instead
          </button>
        </div>
      )}
    </div>
  );
}
